import { FC, ReactNode } from 'react'
import Box from '@mui/material/Box'
import type { SxProps, Theme } from '@mui/material/styles'
import { Loading } from './Loading'

export interface PendableProps {
    pending: boolean
    children: ReactNode
    cover?: boolean
    id?: string
    /** Extra styles for the wrapper that `cover` renders around the children */
    sx?: SxProps<Theme>
}

/**
 * Component to display 'loading' state on component (we wrap component with it)
 * @param pending - when we should show the 'loading' state
 * @param children - component to wrap around
 * @param cover - whether we should show component covered with shadow and 'loading' spinner on it, otherwise spinner replaces component
 * @param id
 * @param sx - extra styles for the wrapper rendered with `cover`
 */
const Pendable: FC<PendableProps> = ({ pending, children, cover, id, sx }) => {
    return !cover ? (
        pending ? (
            <Loading loading={pending} data-test-id={id ? `pendable-${id}` : undefined} />
        ) : (
            <>{children}</>
        )
    ) : (
        <Box sx={[{ position: 'relative', width: '100%', height: '100%' }, ...(Array.isArray(sx) ? sx : [sx])]}>
            {children}
            {pending && (
                <Box
                    sx={{
                        position: 'absolute',
                        inset: 0,
                        // Keep the cover above sticky table headers and pagination (z-index: 2).
                        zIndex: 3,
                    }}
                >
                    <Loading loading={pending} data-test-id={id ? `pendable-${id}` : undefined} />
                </Box>
            )}
        </Box>
    )
}

export default Pendable
